/**
 * Custom hook for managing countdown timer
 */

import { useState, useEffect, useCallback, useRef } from 'react';

export interface UseTimerReturn {
  timeLeft: number;
  maxTime: number;
  progress: number;
  isRunning: boolean;
  startTimer: (duration: number) => void;
  pauseTimer: () => void;
  resumeTimer: () => void;
  resetTimer: () => void;
}

export function useTimer(onTimeout?: () => void): UseTimerReturn {
  const [timeLeft, setTimeLeft] = useState(0);
  const [maxTime, setMaxTime] = useState(0);
  const [isRunning, setIsRunning] = useState(false);

  const intervalRef = useRef<number | null>(null);
  const onTimeoutRef = useRef(onTimeout);

  // Keep callback ref up to date
  useEffect(() => {
    onTimeoutRef.current = onTimeout;
  }, [onTimeout]);

  const clearTimer = useCallback(() => {
    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const startTimer = useCallback((duration: number) => {
    clearTimer();
    setMaxTime(duration);
    setTimeLeft(duration);
    setIsRunning(true);
  }, [clearTimer]);

  const pauseTimer = useCallback(() => {
    clearTimer();
    setIsRunning(false);
  }, [clearTimer]);

  const resumeTimer = useCallback(() => {
    if (timeLeft > 0) {
      setIsRunning(true);
    }
  }, [timeLeft]);

  const resetTimer = useCallback(() => {
    clearTimer();
    setTimeLeft(0);
    setMaxTime(0);
    setIsRunning(false);
  }, [clearTimer]);

  // Timer tick effect
  useEffect(() => {
    if (!isRunning || timeLeft <= 0) {
      return;
    }

    intervalRef.current = window.setInterval(() => {
      setTimeLeft(prev => {
        const newTime = prev - 10; // Update every 10ms for smooth progress bar

        if (newTime <= 0) {
          clearInterval(intervalRef.current!);
          intervalRef.current = null;
          setIsRunning(false);

          // Call timeout callback
          if (onTimeoutRef.current) {
            onTimeoutRef.current();
          }

          return 0;
        }

        return newTime;
      });
    }, 10);

    return () => {
      if (intervalRef.current !== null) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isRunning, timeLeft]);

  // Calculate progress (0 to 1)
  const progress = maxTime > 0 ? timeLeft / maxTime : 0;

  return {
    timeLeft,
    maxTime,
    progress,
    isRunning,
    startTimer,
    pauseTimer,
    resumeTimer,
    resetTimer,
  };
}
